import { Injectable, inject } from '@angular/core';

import { Observable, map } from 'rxjs';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { HasActiveConsumableSubscriptionGqlService } from '@hdms/financial-hub/data-access';
import { Permission } from '@hdms/shared/common-types';
import { FT_GLOBAL_AUTHORIZATION_MANAGEMENT, PermissionService, UserContextService } from '@hdms/shared/data-access';
import { EnvironmentService, FeatureToggleService } from '@hdms/shared/util-core';

import {
  FT_AM_ENABLED_FLAG,
  FT_CCP_LINK,
  FT_CONSOPASS_REPORTING,
  FT_JUMP_TO_BYD,
  FT_SOFTWARE_SELLING
} from '../constants';
import { FinHubPermissions } from '../types/fin-hub-permissions.enum';

@Injectable({
  providedIn: 'root'
})
export class AccessService {
  private readonly environmentService = inject(EnvironmentService);
  private readonly userContext = inject(UserContextService);
  private readonly permService = inject(PermissionService);
  private readonly ftService = inject(FeatureToggleService);
  private readonly consumableSubscriptionStatusService = inject(HasActiveConsumableSubscriptionGqlService);

  private isConsopassReportingEnabled: boolean;

  get isByDesign(): boolean {
    return this.environmentService.isByDesign;
  }

  get isConsopassReportingLinkEnabled(): boolean {
    return this.ftService.getToggleById(FT_CONSOPASS_REPORTING) && this.isConsopassReportingEnabled;
  }

  get includeSoftwareSelling(): boolean {
    return this.ftService.getToggleById(FT_SOFTWARE_SELLING);
  }

  get isCcpLinkEnabled(): boolean {
    return this.ftService.getToggleById(FT_CCP_LINK);
  }

  get isAccountManager(): boolean {
    return this.userContext.isAccountManager;
  }

  get includeQuoteForContract(): boolean {
    return this.isAccountManager && this.isByDesign && this.ftService.getToggleById(FT_JUMP_TO_BYD);
  }

  public isFinHubAccessPermitted$(): Observable<boolean> {
    return this.userContext.update$().pipe(
      map(() => {
        if (this.ftService.getToggleById(FT_GLOBAL_AUTHORIZATION_MANAGEMENT)) {
          return this.permService.checkPermission(FinHubPermissions.ACCESS_FINANCIAL_HUB, true);
        }

        if (!this.ftService.getToggleById(FT_AM_ENABLED_FLAG) || !this.userContext.isAuthorizationManagementEnabled) {
          return true;
        }
        return this.permService.checkPermission(Permission.VIEW_CUSTOMER_SPECIFIC_PRICING);
      })
    );
  }

  public getConsumableSubscriptionStatus$(): Observable<boolean> {
    return this.consumableSubscriptionStatusService
      .fetch()
      .pipe(map(response => (this.isConsopassReportingEnabled = response.data.hasActiveConsumablesSubscriptions)));
  }
}
